import _ from 'lodash';

import { HITL } from '@/api';
import { AgentStatus, ISettings } from '@/types';
import {
  useAgentStatusSettingsStore,
  useAgentStatusSettingsSetters,
} from './use-agent-status-settings-store';
import { migrateToV2Settings, CURRENT_VERSION } from '../common';
import { SETTINGS_CATEGORIES, DEFAULT_STATUS_KEY } from '@/constants';
import { UIEventBus, THROW_API_ERROR } from '@/common/ui-event-bus';

/** TODO: Find a way to mock API in stories? */
export const SDK = { HITL };
export const useAgentStatusSettingsActions = () => {
  const store = useAgentStatusSettingsStore();
  const setters = useAgentStatusSettingsSetters();

  const loadAgentStatusSettings = async () => {
    const response = await SDK.HITL.settings.getSettings({
      categories: [SETTINGS_CATEGORIES.STATUS],
      isCommon: true,
    });

    const category = response.data[SETTINGS_CATEGORIES.STATUS];
    const categoryData = (category as ISettings).data;
    const migrated = migrateToV2Settings(categoryData);

    return { raw: categoryData, migrated };
  };

  const initializeAgentStatusSettings = async () => {
    try {
      store.isInitializing = true;
      const { migrated: settings } = await loadAgentStatusSettings();

      store.settings = settings;
      store.statusesById = settings.statuses || {};
      store.defaultStatusKey = settings.defaultStatusKey || '';
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      throw error;
    } finally {
      store.isInitializing = false;
    }
  };

  /** Save UI state in the database */
  const updateStatusSettings = async (status?: AgentStatus) => {
    try {
      /* Clear temporary property used by OrList */
      const statuses = store.statusOptions.map(item => {
        return _.omit(item, ['vforkey']);
      });

      if (status) {
        statuses.push(status);
      }

      const settings = {
        ...store.settings,
        version: CURRENT_VERSION,
        statuses: _.keyBy(statuses, 'key'),
        defaultStatusKey: store.defaultStatusKey,
      };

      const data = { [SETTINGS_CATEGORIES.STATUS]: settings };
      await SDK.HITL.settings.updateSettings({ data, isCommon: true });

      store.settings = settings;
      store.statusesById = settings.statuses;
      store.defaultStatusKey = settings.defaultStatusKey;
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      throw error;
    }
  };

  /** Item is added to list after successful save */
  const onAddAgentStatus = async () => {
    try {
      store.isUpdatingId = store.targetItem.key;
      store.isUpdatingType = 'create';

      await updateStatusSettings(store.targetItem);
      setters.onCancelTargetItem();
    } finally {
      store.isUpdatingId = '';
      store.isUpdatingType = '';
    }
  };

  const onUpdateAgentStatus = async () => {
    try {
      const copy = { ...store.statusesById };
      delete copy[store.targetKey];
      copy[store.targetItem.key] = store.targetItem;

      store.isUpdatingId = store.targetItem.key;
      store.isUpdatingType = 'update';
      store.statusesById = copy;

      /* When we rename default key */
      if (store.targetKey === store.defaultStatusKey) {
        store.defaultStatusKey = store.targetItem.key;
      }

      await updateStatusSettings();
      setters.onCancelTargetItem();
    } finally {
      store.isUpdatingId = '';
      store.isUpdatingType = '';
    }
  };

  const onDeleteAgentStatus = async () => {
    try {
      store.isUpdatingId = store.targetKey;
      store.isUpdatingType = 'delete';

      const copy = { ...store.statusesById };
      delete copy[store.targetKey];
      store.statusesById = copy;

      if (store.targetKey === store.defaultStatusKey) {
        store.defaultStatusKey = DEFAULT_STATUS_KEY;
      }

      await updateStatusSettings();
      setters.onCancelTargetItem();
    } finally {
      store.isUpdatingId = '';
      store.isUpdatingType = '';
    }
  };

  /** OrList component can emit this event when adding or removing new items */
  const onItemsOrderChange = async (items: AgentStatus[]) => {
    const statuses = items.map((item, index) => {
      return _.omit({ ...item, order: index }, ['vforkey']);
    }) as AgentStatus[];

    const statusesById = _.keyBy(statuses, 'key');
    if (_.isEqual(statusesById, store.statusesById)) {
      return;
    }

    try {
      store.isUpdatingType = 'items';
      store.statusesById = statusesById;
      await updateStatusSettings();
    } finally {
      store.isUpdatingType = '';
    }
  };

  const onDefaultKeyChange = async (key: string) => {
    try {
      store.isUpdatingType = 'items';
      store.defaultStatusKey = key;
      await updateStatusSettings();
    } finally {
      store.isUpdatingType = '';
    }
  };

  return {
    loadAgentStatusSettings,
    initializeAgentStatusSettings,
    updateStatusSettings,
    onItemsOrderChange,
    onDefaultKeyChange,
    onAddAgentStatus,
    onUpdateAgentStatus,
    onDeleteAgentStatus,
  };
};
